import { can, type Capability, type Member } from "@love-cpmd/core";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { FirstPasswordScreen } from "../components/auth/FirstPasswordScreen";
import { FullPageLoader } from "../components/common/FullPageLoader";
import * as api from "./api";
import { useOnline } from "./hooks";
import { storageGet, storageRemove, storageSet } from "./safe-storage";
import { useMembers } from "./queries";

/**
 * Who is signed in.
 *  - Online (VITE_API_MODE=http): Better Auth keeps the session in an HttpOnly cookie
 *    ("Keep me signed in" = 30 days). On start the app asks the API who the cookie belongs to.
 *  - Prototype (mock): the session is a small record in this browser's storage.
 */
const HTTP = api.API_MODE === "http";
const SESSION_KEY = "love-cpmd:session";
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

/** Online start-up: a sleeping free server answers after up to a minute or so, so keep asking for a while. */
async function whoIsSignedIn(): Promise<Member | null> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await api.currentUser();
    } catch (error) {
      if (attempt >= 30) throw error;
      await new Promise((r) => setTimeout(r, attempt < 3 ? 1000 : 3000));
    }
  }
}

interface StoredSession {
  userId: string;
  expiresAt: number;
}

function readSession(): StoredSession | null {
  for (const which of ["local", "session"] as const) {
    const raw = storageGet(which, SESSION_KEY);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as StoredSession;
      if (parsed.expiresAt > Date.now()) return parsed;
    } catch {
      /* corrupted → drop it */
    }
    storageRemove(which, SESSION_KEY);
  }
  return null;
}

function clearSession() {
  storageRemove("local", SESSION_KEY);
  storageRemove("session", SESSION_KEY);
}

interface AuthContextValue {
  userId: string | null;
  /** False while the online version checks the login cookie at start-up. */
  ready: boolean;
  /** Online version: the member must choose a new password before using the app. */
  mustChangePassword: boolean;
  login: (memberId: string, password: string, remember: boolean) => Promise<Member>;
  logout: () => void;
  /** Re-reads the signed-in member from the server (after a password change). */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [userId, setUserId] = useState<string | null>(() => (HTTP ? null : (readSession()?.userId ?? null)));
  const [mustChangePassword, setMustChange] = useState(false);
  const [ready, setReady] = useState(!HTTP);

  const apply = useCallback((member: Member | null) => {
    setUserId(member?.id ?? null);
    setMustChange(!!member?.mustChangePassword);
  }, []);

  // Online: who does the login cookie belong to?
  useEffect(() => {
    if (!HTTP) return;
    let alive = true;
    whoIsSignedIn()
      .then((member) => alive && apply(member))
      .catch(() => alive && apply(null))
      .finally(() => alive && setReady(true));
    return () => {
      alive = false;
    };
  }, [apply]);

  const login = useCallback(
    async (memberId: string, password: string, remember: boolean) => {
      const member = await api.login(memberId, password, remember);
      if (HTTP) {
        qc.clear();
        apply(member);
        return member;
      }
      clearSession();
      const session: StoredSession = { userId: member.id, expiresAt: Date.now() + (remember ? THIRTY_DAYS : 12 * 60 * 60 * 1000) };
      storageSet(remember ? "local" : "session", SESSION_KEY, JSON.stringify(session));
      setUserId(member.id);
      return member;
    },
    [qc, apply],
  );

  const logout = useCallback(() => {
    if (HTTP) {
      void api.signOut();
      apply(null);
      qc.clear();
      return;
    }
    clearSession();
    setUserId(null);
    qc.removeQueries({ queryKey: ["db", "notifications"] });
  }, [qc, apply]);

  const refresh = useCallback(async () => {
    if (!HTTP) return;
    apply(await api.currentUser());
  }, [apply]);

  useEffect(() => {
    if (HTTP) {
      // The server ended the session (signed out elsewhere, password reset, account deactivated).
      const onUnauthorized = () => {
        apply(null);
        qc.clear();
      };
      window.addEventListener(api.UNAUTHORIZED_EVENT, onUnauthorized);
      return () => window.removeEventListener(api.UNAUTHORIZED_EVENT, onUnauthorized);
    }
    // Prototype: logging out in another tab logs out here too.
    const onStorage = (e: StorageEvent) => {
      if (e.key === SESSION_KEY && !readSession()) setUserId(null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [qc, apply]);

  const value = useMemo(() => ({ userId, ready, mustChangePassword, login, logout, refresh }), [userId, ready, mustChangePassword, login, logout, refresh]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** The signed-in member (live from data, so role changes apply immediately). */
export function useCurrentUser(): Member | undefined {
  const { userId } = useAuth();
  const { data } = useMembers(!!userId);
  return data?.find((m) => m.id === userId);
}

/** Role capability + offline state. Offline: data stays readable, changing actions are disabled. */
export function useAccess() {
  const user = useCurrentUser();
  const online = useOnline();
  return {
    user,
    role: user?.role,
    online,
    can: (cap: Capability) => can(user?.role, cap),
    /** Show the action at all (role). */
    show: (cap: Capability) => can(user?.role, cap),
    /** Action is usable right now (role + online). */
    enabled: (cap: Capability) => can(user?.role, cap) && online,
  };
}

/** F2 route guard: not logged in → /login?next=… ; first login → choose a password. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { userId, ready, mustChangePassword, logout } = useAuth();
  const location = useLocation();
  const { data: members } = useMembers(!!userId && !mustChangePassword);
  const user = members?.find((m) => m.id === userId);

  useEffect(() => {
    if (userId && members && (!user || !user.active)) logout();
  }, [userId, members, user, logout]);

  if (!ready) return <FullPageLoader />;
  if (!userId) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (mustChangePassword) return <FirstPasswordScreen />;
  return <>{children}</>;
}

/** Non-System Managers opening /admin go back to the dashboard. */
export function RequireCapability({ cap, children }: { cap: Capability; children: ReactNode }) {
  const user = useCurrentUser();
  if (!user) return null;
  if (!can(user.role, cap)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
